import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Target,
  MapPin,
  Building2,
  Star,
  Navigation,
  Calendar,
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  UserPlus
} from "lucide-react";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function StudentMatches() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [student, setStudent] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [assigning, setAssigning] = useState(null);
  
  // Assignment dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Request dialog state
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedRotation, setSelectedRotation] = useState("");
  
  // Rotation type options mapping
  const rotationOptions = {
    "TCE1_JAN_APR_2027": { type: "TCE 1", start: "2027-01-11", end: "2027-04-02", label: "TCE 1 – 12 weeks Jan 11 – Apr 2, 2027" },
    "TCE2_JAN_APR_2027": { type: "TCE 2", start: "2027-01-11", end: "2027-04-02", label: "TCE 2 – 12 weeks Jan 11 – Apr 2, 2027" },
    "TC1_JAN_APR_2027": { type: "TC I", start: "2027-01-25", end: "2027-04-16", label: "TC I – 12 weeks Jan 25 – Apr 16, 2027" },
    "TC2_JAN_MAR_2027": { type: "TC II", start: "2027-01-11", end: "2027-03-19", label: "TC II – 10 weeks Jan 11 – Mar 19, 2027" },
    "TC3_JAN_MAR_2027": { type: "TC III", start: "2027-01-11", end: "2027-03-19", label: "TC III – 10 weeks Jan 11 – Mar 19, 2027" },
    "TCE1_MAY_AUG_2027": { type: "TCE 1", start: "2027-05-10", end: "2027-08-03", label: "TCE 1 – 12 weeks May 10 – Aug 3, 2027" },
    "TCE2_MAY_AUG_2027": { type: "TCE 2", start: "2027-05-10", end: "2027-08-03", label: "TCE 2 – 12 weeks May 10 – Aug 3, 2027" },
    "TC1_MAY_AUG_2027": { type: "TC I", start: "2027-05-24", end: "2027-08-13", label: "TC I – 12 weeks May 24 – Aug 13, 2027" },
    "TC2_MAY_JUL_2027": { type: "TC II", start: "2027-05-10", end: "2027-07-16", label: "TC II – 10 weeks May 10 – Jul 16, 2027" },
    "TC3_MAY_JUL_2027": { type: "TC III", start: "2027-05-10", end: "2027-07-16", label: "TC III – 10 weeks May 10 – Jul 16, 2027" },
    "TCE1_SEP_DEC_2027": { type: "TCE 1", start: "2027-09-07", end: "2027-12-03", label: "TCE 1 – 12 weeks Sep 7 – Dec 3, 2027" },
    "TCE2_SEP_DEC_2027": { type: "TCE 2", start: "2027-09-07", end: "2027-12-03", label: "TCE 2 – 12 weeks Sep 7 – Dec 3, 2027" },
    "TC1_SEP_DEC_2027": { type: "TC I", start: "2027-09-20", end: "2027-12-10", label: "TC I – 12 weeks Sep 20 – Dec 10, 2027" },
    "TC2_SEP_NOV_2027": { type: "TC II", start: "2027-09-07", end: "2027-11-12", label: "TC II – 10 weeks Sep 7 – Nov 12, 2027" },
    "TC3_SEP_NOV_2027": { type: "TC III", start: "2027-09-07", end: "2027-11-12", label: "TC III – 10 weeks Sep 7 – Nov 12, 2027" },
  };
  
  // Preferences
  const [maxDistance, setMaxDistance] = useState(75);
  const [preferredStates, setPreferredStates] = useState([]);
  const [totalSites, setTotalSites] = useState(0);

  useEffect(() => {
    if (studentId) {
      fetchMatches();
    }
  }, [studentId]);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const statesParam = preferredStates.length > 0 ? preferredStates.join(",") : "";
      const response = await api.get(
        `/matching/student/${studentId}?max_distance=${maxDistance}&limit=15${statesParam ? `&preferred_states=${statesParam}&filter_by_state=true` : ""}`
      );
      setStudent({ id: response.data.student_id, name: response.data.student_name });
      setMatches(response.data.matches);
      setTotalSites(response.data.total_sites_analyzed);
    } catch (error) {
      toast.error("Failed to load matches");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRequestDialog = (match) => {
    setSelectedMatch(match);
    setSelectedRotation("");
    setRequestDialogOpen(true);
  };

  const handleSubmitRequest = async () => {
    if (!selectedMatch || !selectedRotation) return;
    
    const rotation = rotationOptions[selectedRotation];
    if (!rotation) return;
    
    setSubmitting(selectedMatch.clinical_site_id);
    try {
      // Get student full data for the request
      const studentRes = await api.get(`/students/${studentId}`);
      const studentData = studentRes.data;
      
      // Create placement request
      await api.post("/placement-requests", {
        student_id: studentData.id,
        clinical_site_id: selectedMatch.clinical_site_id,
        requested_start_date: rotation.start,
        requested_end_date: rotation.end,
        rotation_type: rotation.label,
        request_notes: `Matched via preference algorithm (Score: ${selectedMatch.total_score})`
      });
      
      toast.success(`Placement request submitted for ${selectedMatch.clinical_site_name}`);
      setRequestDialogOpen(false);
      navigate("/requests");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(null);
    }
  };

  const handleOpenAssignDialog = async (match) => {
    setSelectedMatch(match);
    setAssignDialogOpen(true);
    setLoadingSlots(true);
    setSelectedSlot("");
    setSelectedRotation("");
    
    try {
      // Fetch available slots for this clinical site
      const response = await api.get(`/slots?clinical_site_id=${match.clinical_site_id}`);
      const slots = response.data.filter(slot => 
        slot.capacity > (slot.filled || 0) && !slot.is_locked
      );
      setAvailableSlots(slots);
    } catch (error) {
      toast.error("Failed to load available slots");
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleAssignStudent = async () => {
    if (!selectedSlot || !selectedMatch || !selectedRotation) return;
    
    setAssigning(selectedMatch.clinical_site_id);
    try {
      // Get student full data
      const studentRes = await api.get(`/students/${studentId}`);
      const studentData = studentRes.data;
      
      // Create placement (direct assignment)
      await api.post("/placements", {
        student_id: studentData.id,
        slot_id: selectedSlot
      });
      
      toast.success(`${student?.name} assigned to ${selectedMatch.clinical_site_name}`);
      setAssignDialogOpen(false);
      navigate("/approvals");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign student");
    } finally {
      setAssigning(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-400";
    if (score >= 60) return "text-cyan-400";
    if (score >= 40) return "text-yellow-400";
    return "text-slate-400";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-emerald-400/10 border-emerald-400/30";
    if (score >= 60) return "bg-cyan-400/10 border-cyan-400/30";
    if (score >= 40) return "bg-yellow-400/10 border-yellow-400/30";
    return "bg-slate-400/10 border-slate-400/30";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-matches">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/students")}
            className="text-slate-400 hover:text-slate-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
              <Target className="w-6 h-6 text-cyan-400" />
              Smart Matches
            </h1>
            <p className="text-slate-400">
              Best clinical site matches for <span className="text-cyan-400">{student?.name}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Preferences Panel */}
      <Card className="bg-[#151A23] border-slate-700">
        <CardHeader>
          <CardTitle className="text-slate-100 text-lg">Match Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Max Distance */}
            <div className="space-y-3">
              <Label className="text-slate-300">Maximum Distance: {maxDistance} miles</Label>
              <Slider
                value={[maxDistance]}
                onValueChange={(value) => setMaxDistance(value[0])}
                min={25}
                max={200}
                step={25}
                className="py-2"
              />
              <p className="text-xs text-slate-500">Target: 75 miles or less for best matches</p>
            </div>

            {/* Preferred States */}
            <div className="space-y-3">
              <Label className="text-slate-300">Preferred States</Label>
              <Select
                value={preferredStates[0] || "any"}
                onValueChange={(value) => {
                  if (value === "any") {
                    setPreferredStates([]);
                  } else if (!preferredStates.includes(value)) {
                    setPreferredStates([...preferredStates, value]);
                  }
                }}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="bg-[#151A23] border-slate-700 max-h-60">
                  <SelectItem value="any" className="text-slate-200">Any State</SelectItem>
                  {US_STATES.map(state => (
                    <SelectItem key={state} value={state} className="text-slate-200">{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {preferredStates.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {preferredStates.map(state => (
                    <Badge 
                      key={state}
                      className="bg-cyan-400/10 text-cyan-400 cursor-pointer hover:bg-red-400/20 hover:text-red-400"
                      onClick={() => setPreferredStates(preferredStates.filter(s => s !== state))}
                    >
                      {state} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Apply Button */}
            <div className="flex items-center">
              <Button 
                onClick={fetchMatches}
                className="bg-cyan-400 text-black hover:bg-cyan-300 w-full mt-6"
                data-testid="apply-preferences-btn"
              >
                <Target className="w-4 h-4 mr-2" />
                Update Matches
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-slate-400">
          Showing <span className="text-slate-200 font-semibold">{matches.length}</span> matches 
          from <span className="text-slate-200">{totalSites}</span> clinical sites analyzed
        </p>
      </div>

      {/* Match Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {matches.map((match, index) => (
          <Card 
            key={match.clinical_site_id}
            className={`bg-[#151A23] border ${getScoreBg(match.total_score)} hover:border-cyan-400/50 transition-all`}
            data-testid={`match-card-${index}`}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {/* Rank Badge */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                    index === 0 ? "bg-yellow-400/20 text-yellow-400" :
                    index === 1 ? "bg-slate-300/20 text-slate-300" :
                    index === 2 ? "bg-amber-600/20 text-amber-600" :
                    "bg-slate-700 text-slate-400"
                  }`}>
                    #{index + 1}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100">{match.clinical_site_name}</h3>
                    <p className="text-sm text-slate-400">{match.short_id}</p>
                  </div>
                </div>
                
                {/* Score */}
                <div className="text-right">
                  <p className={`text-2xl font-bold ${getScoreColor(match.total_score)}`}>
                    {match.total_score.toFixed(0)}
                  </p>
                  <p className="text-xs text-slate-500">match score</p>
                </div>
              </div>

              {/* Site Details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-300">
                    {match.site_details?.city}, {match.site_details?.state}
                  </span>
                </div>
                
                {/* Distance Info */}
                <div className="flex items-center gap-2 text-sm">
                  <Navigation className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-300">
                    {match.driving_distance_miles ? (
                      <>
                        <span className="text-cyan-400 font-medium">{match.driving_distance_miles} mi</span>
                        {match.driving_time_minutes && (
                          <span className="text-slate-400 ml-2">
                            ({Math.floor(match.driving_time_minutes / 60)}h {match.driving_time_minutes % 60}m drive)
                          </span>
                        )}
                      </>
                    ) : match.distance_miles ? (
                      <span>{match.distance_miles} mi (straight line)</span>
                    ) : (
                      <span className="text-slate-500">Distance unavailable</span>
                    )}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-300">{match.site_details?.organization}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Star className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-300">{match.site_details?.specialty}</span>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="text-center p-2 bg-slate-800/50 rounded">
                  <p className="text-xs text-slate-500">Distance</p>
                  <p className="text-sm font-semibold text-slate-200">{match.distance_score}/60</p>
                </div>
                <div className="text-center p-2 bg-slate-800/50 rounded">
                  <p className="text-xs text-slate-500">Specialty</p>
                  <p className="text-sm font-semibold text-slate-200">{match.specialty_score}/25</p>
                </div>
                <div className="text-center p-2 bg-slate-800/50 rounded">
                  <p className="text-xs text-slate-500">City</p>
                  <p className="text-sm font-semibold text-slate-200">{match.state_score}/15</p>
                </div>
              </div>

              {/* Match Reasons */}
              <div className="flex flex-wrap gap-1 mb-4">
                {match.match_reasons.slice(0, 3).map((reason, i) => (
                  <Badge key={i} className="bg-slate-800 text-slate-300 text-xs">
                    {reason}
                  </Badge>
                ))}
              </div>

              {/* Action */}
              <div className="flex items-center justify-end pt-3 border-t border-slate-700">
                {(user?.role === "admin" || user?.role === "university") && (
                  <Button
                    size="sm"
                    onClick={() => handleOpenRequestDialog(match)}
                    disabled={submitting === match.clinical_site_id}
                    className="bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20 border border-cyan-400/30"
                    data-testid={`submit-request-btn-${index}`}
                  >
                    {submitting === match.clinical_site_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Request Placement
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {matches.length === 0 && (
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="p-8 text-center">
            <Target className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No matches found with current preferences</p>
            <p className="text-sm text-slate-500 mt-1">Try adjusting your distance or state preferences</p>
          </CardContent>
        </Card>
      )}

      {/* Assignment Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-400" />
              Assign Student to Rotation
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-800/50 p-3 rounded-lg">
              <p className="text-sm text-slate-400">Student</p>
              <p className="text-slate-100 font-medium">{student?.name}</p>
            </div>
            
            <div className="bg-slate-800/50 p-3 rounded-lg">
              <p className="text-sm text-slate-400">Clinical Site</p>
              <p className="text-slate-100 font-medium">{selectedMatch?.clinical_site_name}</p>
              <p className="text-xs text-slate-500 mt-1">
                {selectedMatch?.site_details?.city}, {selectedMatch?.site_details?.state}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Rotation Type and Dates</Label>
              <Select value={selectedRotation} onValueChange={setSelectedRotation}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200">
                  <SelectValue placeholder="Select rotation type and dates..." />
                </SelectTrigger>
                <SelectContent className="bg-[#151A23] border-slate-700 max-h-[300px]">
                  {/* Spring 2027 */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Spring 2027</div>
                  <SelectItem value="TCE1_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 1 – 12 weeks Jan 11 – Apr 2, 2027
                  </SelectItem>
                  <SelectItem value="TCE2_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 2 – 12 weeks Jan 11 – Apr 2, 2027
                  </SelectItem>
                  <SelectItem value="TC1_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800">
                    TC I – 12 weeks Jan 25 – Apr 16, 2027
                  </SelectItem>
                  <SelectItem value="TC2_JAN_MAR_2027" className="text-slate-200 focus:bg-slate-800">
                    TC II – 10 weeks Jan 11 – Mar 19, 2027
                  </SelectItem>
                  <SelectItem value="TC3_JAN_MAR_2027" className="text-slate-200 focus:bg-slate-800">
                    TC III – 10 weeks Jan 11 – Mar 19, 2027
                  </SelectItem>
                  {/* Summer 2027 */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Summer 2027</div>
                  <SelectItem value="TCE1_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 1 – 12 weeks May 10 – Aug 3, 2027
                  </SelectItem>
                  <SelectItem value="TCE2_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 2 – 12 weeks May 10 – Aug 3, 2027
                  </SelectItem>
                  <SelectItem value="TC1_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800">
                    TC I – 12 weeks May 24 – Aug 13, 2027
                  </SelectItem>
                  <SelectItem value="TC2_MAY_JUL_2027" className="text-slate-200 focus:bg-slate-800">
                    TC II – 10 weeks May 10 – Jul 16, 2027
                  </SelectItem>
                  <SelectItem value="TC3_MAY_JUL_2027" className="text-slate-200 focus:bg-slate-800">
                    TC III – 10 weeks May 10 – Jul 16, 2027
                  </SelectItem>
                  {/* Fall 2027 */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Fall 2027</div>
                  <SelectItem value="TCE1_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 1 – 12 weeks Sep 7 – Dec 3, 2027
                  </SelectItem>
                  <SelectItem value="TCE2_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 2 – 12 weeks Sep 7 – Dec 3, 2027
                  </SelectItem>
                  <SelectItem value="TC1_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800">
                    TC I – 12 weeks Sep 20 – Dec 10, 2027
                  </SelectItem>
                  <SelectItem value="TC2_SEP_NOV_2027" className="text-slate-200 focus:bg-slate-800">
                    TC II – 10 weeks Sep 7 – Nov 12, 2027
                  </SelectItem>
                  <SelectItem value="TC3_SEP_NOV_2027" className="text-slate-200 focus:bg-slate-800">
                    TC III – 10 weeks Sep 7 – Nov 12, 2027
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Select Rotation Slot</Label>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : availableSlots.length > 0 ? (
                <Select value={selectedSlot} onValueChange={setSelectedSlot}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200">
                    <SelectValue placeholder="Choose a rotation slot" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151A23] border-slate-700">
                    {availableSlots.map((slot) => (
                      <SelectItem 
                        key={slot.id} 
                        value={slot.id}
                        className="text-slate-200 focus:bg-slate-800"
                      >
                        <div className="flex flex-col">
                          <span>{slot.title}</span>
                          <span className="text-xs text-slate-400">
                            {slot.start_date} - {slot.end_date} ({slot.capacity - (slot.filled || 0)} spots left)
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-yellow-400">No available slots found</p>
              )}
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button 
              variant="ghost" 
              onClick={() => setAssignDialogOpen(false)}
              className="text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAssignStudent}
              disabled={!selectedSlot || !selectedRotation || assigning}
              className="bg-emerald-400 text-black hover:bg-emerald-300"
            >
              {assigning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Assign Student
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Placement Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Send className="w-5 h-5 text-cyan-400" />
              Request Placement
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-800/50 p-3 rounded-lg">
              <p className="text-sm text-slate-400">Student</p>
              <p className="text-slate-100 font-medium">{student?.name}</p>
            </div>
            
            <div className="bg-slate-800/50 p-3 rounded-lg">
              <p className="text-sm text-slate-400">Clinical Site</p>
              <p className="text-slate-100 font-medium">{selectedMatch?.clinical_site_name}</p>
              <p className="text-xs text-slate-500 mt-1">
                {selectedMatch?.site_details?.city}, {selectedMatch?.site_details?.state}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Rotation Type and Dates</Label>
              <Select value={selectedRotation} onValueChange={setSelectedRotation}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200">
                  <SelectValue placeholder="Select rotation type and dates..." />
                </SelectTrigger>
                <SelectContent className="bg-[#151A23] border-slate-700 max-h-[300px]">
                  {/* Spring 2027 */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Spring 2027</div>
                  <SelectItem value="TCE1_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 1 – 12 weeks Jan 11 – Apr 2, 2027
                  </SelectItem>
                  <SelectItem value="TCE2_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 2 – 12 weeks Jan 11 – Apr 2, 2027
                  </SelectItem>
                  <SelectItem value="TC1_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800">
                    TC I – 12 weeks Jan 25 – Apr 16, 2027
                  </SelectItem>
                  <SelectItem value="TC2_JAN_MAR_2027" className="text-slate-200 focus:bg-slate-800">
                    TC II – 10 weeks Jan 11 – Mar 19, 2027
                  </SelectItem>
                  <SelectItem value="TC3_JAN_MAR_2027" className="text-slate-200 focus:bg-slate-800">
                    TC III – 10 weeks Jan 11 – Mar 19, 2027
                  </SelectItem>
                  {/* Summer 2027 */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Summer 2027</div>
                  <SelectItem value="TCE1_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 1 – 12 weeks May 10 – Aug 3, 2027
                  </SelectItem>
                  <SelectItem value="TCE2_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 2 – 12 weeks May 10 – Aug 3, 2027
                  </SelectItem>
                  <SelectItem value="TC1_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800">
                    TC I – 12 weeks May 24 – Aug 13, 2027
                  </SelectItem>
                  <SelectItem value="TC2_MAY_JUL_2027" className="text-slate-200 focus:bg-slate-800">
                    TC II – 10 weeks May 10 – Jul 16, 2027
                  </SelectItem>
                  <SelectItem value="TC3_MAY_JUL_2027" className="text-slate-200 focus:bg-slate-800">
                    TC III – 10 weeks May 10 – Jul 16, 2027
                  </SelectItem>
                  {/* Fall 2027 */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Fall 2027</div>
                  <SelectItem value="TCE1_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 1 – 12 weeks Sep 7 – Dec 3, 2027
                  </SelectItem>
                  <SelectItem value="TCE2_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 2 – 12 weeks Sep 7 – Dec 3, 2027
                  </SelectItem>
                  <SelectItem value="TC1_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800">
                    TC I – 12 weeks Sep 20 – Dec 10, 2027
                  </SelectItem>
                  <SelectItem value="TC2_SEP_NOV_2027" className="text-slate-200 focus:bg-slate-800">
                    TC II – 10 weeks Sep 7 – Nov 12, 2027
                  </SelectItem>
                  <SelectItem value="TC3_SEP_NOV_2027" className="text-slate-200 focus:bg-slate-800">
                    TC III – 10 weeks Sep 7 – Nov 12, 2027
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button 
              variant="ghost" 
              onClick={() => setRequestDialogOpen(false)}
              className="text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitRequest}
              disabled={!selectedRotation || submitting}
              className="bg-cyan-400 text-black hover:bg-cyan-300"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
